import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { usePortalReady } from '@/data/prewarm';
import { recordSetupEvent } from '@/data/setup-wizard';

/**
 * The done screen's "View your booking page" — readiness-gated (booking-page
 * pre-warm unit). While the host's on-demand cert is still minting it shows a
 * "Building your booking page…" state that polls the real warm status; the
 * moment it's ready the link goes live (opening a warm page, never a cold one).
 * A short reveal-fallback means a stuck probe never traps the button, and
 * "Enter your dashboard" alongside always lets the user carry on regardless.
 */
export function BookingPageLaunch({
  portalUrl,
  fleetId,
}: {
  portalUrl: string;
  fleetId: string | null;
}) {
  const host = portalUrl.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  const { ready, durationMs } = usePortalReady(host, true);
  const recorded = useRef(false);

  // Fallback: never trap the button — reveal the link after 15s even if the
  // probe hasn't confirmed (better a maybe-cold open than a stuck spinner).
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setRevealed(true), 15_000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (ready && !recorded.current && fleetId) {
      recorded.current = true;
      recordSetupEvent(fleetId, 'wizard_booking_page_ready', {
        ...(durationMs !== null ? { durationMs } : {}),
      });
    }
  }, [ready, durationMs, fleetId]);

  if (!ready && !revealed) {
    return (
      <Button variant="secondary" className="w-full" size="lg" disabled>
        <Spinner size="sm" /> Building your booking page…
      </Button>
    );
  }

  return (
    <a href={portalUrl} target="_blank" rel="noreferrer" className="block">
      <Button variant="secondary" className="w-full" size="lg">
        View your booking page ↗
      </Button>
    </a>
  );
}
